document.addEventListener('DOMContentLoaded', function () {
  const groupedColorPageCache = new Map();

  function getGroupedColorPage(url) {
    const cacheKey = url.toString();

    if (!groupedColorPageCache.has(cacheKey)) {
      const request = fetch(cacheKey)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error('Unable to load color filter results.');
          }

          const parsedDocument = new DOMParser().parseFromString(
            await response.text(),
            'text/html'
          );
          const $productList = parsedDocument.querySelector(
            'product-list[data-yg-facet-pages]'
          );

          if (!$productList) {
            if (parsedDocument.querySelector('.empty-state')) {
              return { pages: 1, products: new Map() };
            }

            throw new Error('Color filter results are unavailable.');
          }

          const products = new Map();

          $productList.querySelectorAll('.yg-product-card').forEach(($card) => {
            const variants = JSON.parse(
              $card.querySelector('[data-yg-product-card-variants]')
                ?.textContent || '[]'
            );
            const relevantVariantId = $card.querySelector(
              '.klaviyo-favorites-plp-slot'
            )?.dataset.variantId;
            const relevantVariant = variants.find(
              (variant) => String(variant.id) === relevantVariantId
            );
            const colorPosition = Number($card.dataset.ygColorPosition);
            const color = relevantVariant?.options?.[colorPosition];

            if (color) {
              products.set($card.getAttribute('handle'), {
                color,
                variant: variants.find((variant) => {
                  return (
                    variant.options[colorPosition] === color &&
                    variant.featured_image?.url
                  );
                })
              });
            }
          });

          return {
            pages: Number($productList.dataset.ygFacetPages) || 1,
            products
          };
        })
        .catch((error) => {
          groupedColorPageCache.delete(cacheKey);
          throw error;
        });

      if (groupedColorPageCache.size >= 64) {
        groupedColorPageCache.delete(groupedColorPageCache.keys().next().value);
      }

      groupedColorPageCache.set(cacheKey, request);
    }

    return groupedColorPageCache.get(cacheKey);
  }

  class YGProductCardMedia extends HTMLElement {
    connectedCallback() {
      if (this.abortController && !this.abortController.signal.aborted) {
        return;
      }

      this.productCard = this.closest('.yg-product-card');

      if (!this.productCard) {
        return;
      }

      this.swiperElement = this.querySelector('[data-yg-product-card-swiper]');
      this.variantPreview = this.querySelector(
        '[data-yg-product-card-variant-preview]'
      );
      this.sizeLinks = Array.from(this.querySelectorAll('[data-size-value]'));
      this.variants = this.getVariants();
      this.colorPosition = this.getPosition('ygColorPosition');
      this.sizePosition = this.getPosition('ygSizePosition');
      this.selectedColor = this.getInitialColor();
      this.abortController = new AbortController();

      this.initSwiper();
      this.bindEvents();
      this.updateSizes();
      this.resolveGroupedColor();
    }

    disconnectedCallback() {
      this.hideVariantPreview();
      this.hoveredSwatchRoot = null;

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      if (this.abortController) {
        this.abortController.abort();
      }
    }

    getVariants() {
      const $script = this.productCard.querySelector(
        '[data-yg-product-card-variants]'
      );

      if (!$script) {
        return [];
      }

      try {
        return JSON.parse($script.textContent);
      } catch (error) {
        return [];
      }
    }

    getPosition(key) {
      const value = this.productCard.dataset[key];

      if (value === undefined || value === '') {
        return -1;
      }

      return Number(value);
    }

    getInitialColor() {
      if (this.colorPosition < 0) {
        return '';
      }

      const filteredColor = this.productCard.dataset.ygFilterColor;

      if (filteredColor) {
        return filteredColor;
      }

      const $checkedSwatch = this.productCard.querySelector(
        '.product-card__info [type="radio"][data-variant-id]:checked'
      );
      const checkedVariant = this.getVariantById(
        $checkedSwatch?.dataset.variantId
      );
      const fallbackVariant =
        this.variants.find((variant) => variant.available) || this.variants[0];
      const variant = checkedVariant || fallbackVariant;

      return variant?.options?.[this.colorPosition] || '';
    }

    async resolveGroupedColor() {
      const $script = this.productCard.querySelector(
        '[data-yg-product-card-color-filters]'
      );

      if (!$script || this.colorPosition < 0) {
        return;
      }

      const signal = this.abortController.signal;
      const selectionVersion = this.selectionVersion || 0;

      try {
        const colorFilter = JSON.parse($script.textContent);
        const baseUrl = new URL(window.location.href);

        Array.from(baseUrl.searchParams.keys()).forEach((key) => {
          if (key.startsWith('filter.')) {
            baseUrl.searchParams.delete(key);
          }
        });
        baseUrl.searchParams.delete('page');
        baseUrl.searchParams.set(
          'section_id',
          this.productCard.dataset.ygFacetSectionId
        );
        const handle = this.productCard.getAttribute('handle');

        for (const filterValue of colorFilter.values) {
          if (
            signal.aborted ||
            selectionVersion !== (this.selectionVersion || 0)
          ) {
            return;
          }

          let match;

          if (
            filterValue.value.startsWith('gid://shopify/FilterSettingGroup/')
          ) {
            const url = new URL(baseUrl);
            url.searchParams.set(colorFilter.paramName, filterValue.value);
            let page = 1;
            let pages = 1;

            do {
              if (
                signal.aborted ||
                selectionVersion !== (this.selectionVersion || 0)
              ) {
                return;
              }

              url.searchParams.set('page', page);
              const results = await getGroupedColorPage(url);
              match = results.products.get(handle);
              pages = results.pages;
              page += 1;
            } while (!match && page <= pages);
          } else {
            const normalizedLabel = this.getNormalizedValue(filterValue.label);
            const normalizedValue = this.getNormalizedValue(filterValue.value);
            const matchingVariants = this.variants.filter((variant) => {
              const color = this.getNormalizedValue(
                this.getVariantColor(variant)
              );
              return color === normalizedLabel || color === normalizedValue;
            });

            if (matchingVariants.length > 0) {
              match = {
                color: this.getVariantColor(matchingVariants[0]),
                variant: matchingVariants.find(
                  (variant) => variant.featured_image?.url
                )
              };
            }
          }

          if (match) {
            if (
              !signal.aborted &&
              selectionVersion === (this.selectionVersion || 0)
            ) {
              this.selectedColor = match.color;
              this.updateSizes();
              this.applyFilteredVariantImage(match.variant);
            }

            return;
          }
        }
      } catch (error) {
        // Keep the server-rendered image if grouped filter results cannot be loaded.
      }
    }

    applyFilteredVariantImage(variant, { speed = 0 } = {}) {
      if (!variant?.featured_image?.url) {
        return;
      }

      const $firstSlide =
        this.swiperElement?.querySelector(
          ".swiper-slide[data-media-order='1']:not(.swiper-slide-duplicate)"
        ) ||
        this.swiperElement?.querySelector(
          ".swiper-slide[data-media-order='1']"
        );
      const $firstImage = $firstSlide?.querySelector('img');
      const $fallbackImage = this.querySelector(
        '.product-card__image--primary'
      );

      [$firstImage, $fallbackImage].filter(Boolean).forEach(($image) => {
        $image.removeAttribute('srcset');
        $image.removeAttribute('data-src');
        $image.alt = variant.featured_image.alt;
        $image.width = variant.featured_image.width;
        $image.height = variant.featured_image.height;
        $image.src = variant.featured_image.url;
      });

      // Keep loop duplicates in sync when present
      this.swiperElement
        ?.querySelectorAll(
          ".swiper-slide[data-media-order='1'] img, .swiper-slide-duplicate[data-media-order='1'] img"
        )
        .forEach(($image) => {
          if ($image === $firstImage) {
            return;
          }

          $image.removeAttribute('srcset');
          $image.removeAttribute('data-src');
          $image.alt = variant.featured_image.alt;
          $image.width = variant.featured_image.width;
          $image.height = variant.featured_image.height;
          $image.src = variant.featured_image.url;
        });

      if ($firstSlide && variant.featured_media_position) {
        $firstSlide.dataset.mediaPosition = String(
          variant.featured_media_position
        );

        if (this.mediaPositions) {
          this.mediaPositions[0] = Number(variant.featured_media_position);
        }
      }

      if (this.swiper) {
        this.swiper.update();

        if (this.swiper.params.loop && this.swiper.slideToLoop) {
          this.swiper.slideToLoop(0, speed);
        } else {
          this.swiper.slideTo(0, speed);
        }
      }
    }

    updateCardMedia(variant, speed = 0) {
      if (!variant) {
        return;
      }

      this.hideVariantPreview();

      if (this.slideToVariantMedia(variant, speed)) {
        return;
      }

      this.applyFilteredVariantImage(variant, { speed });
    }

    decodeHtmlEntities(value) {
      if (!value) {
        return '';
      }

      const textarea = document.createElement('textarea');
      textarea.innerHTML = String(value);
      return textarea.value;
    }

    getSwatchColorValue($swatch) {
      const rawValue =
        $swatch.getAttribute('data-value') ||
        $swatch.getAttribute('orig-value') ||
        $swatch.closest('[orig-value]')?.getAttribute('orig-value') ||
        '';

      return this.decodeHtmlEntities(rawValue).trim();
    }

    findVariantByColor(colorValue) {
      if (this.colorPosition < 0 || !colorValue) {
        return null;
      }

      const normalizedColor = this.getNormalizedValue(colorValue);

      return (
        this.variants.find((variant) => {
          return (
            variant.available &&
            this.getNormalizedValue(variant.options?.[this.colorPosition]) ===
              normalizedColor
          );
        }) ||
        this.variants.find((variant) => {
          return (
            this.getNormalizedValue(variant.options?.[this.colorPosition]) ===
            normalizedColor
          );
        }) ||
        null
      );
    }

    getVariantFromSwatchKingEvent(event) {
      const $swatch =
        event.target instanceof Element
          ? event.target.closest(
              '.swatch-selector[data-value], .swatch-view-item[orig-value]'
            )
          : null;

      if (
        !$swatch ||
        !$swatch.closest('variant-swatch-king') ||
        !this.productCard.contains($swatch)
      ) {
        return null;
      }

      return this.findVariantByColor(this.getSwatchColorValue($swatch));
    }

    applyColorVariant(variant, { speed = 0 } = {}) {
      if (!variant) {
        return;
      }

      this.selectionVersion = (this.selectionVersion || 0) + 1;
      this.selectedColor = this.getVariantColor(variant);
      this.updateCardMedia(variant, speed);
      this.updateSizes();
      this.updateProductLinks(variant);
    }

    updateProductLinks(variant) {
      if (!variant?.id) {
        return;
      }

      const variantUrl = variant.url || '';

      this.productCard
        .querySelectorAll('a[href*="/products/"]')
        .forEach(($link) => {
          if ($link.closest('variant-swatch-king')) {
            return;
          }

          if (variantUrl) {
            try {
              const url = new URL(variantUrl, window.location.origin);
              const currentUrl = new URL($link.href, window.location.origin);
              const fromCollection =
                currentUrl.searchParams.get('from_collection');

              if (fromCollection) {
                url.searchParams.set('from_collection', fromCollection);
              }

              $link.href = `${url.pathname}${url.search}${url.hash}`;
              return;
            } catch (error) {
              // Fall through to variant query update
            }
          }

          try {
            const url = new URL($link.href, window.location.origin);
            url.searchParams.set('variant', String(variant.id));
            $link.href = `${url.pathname}${url.search}${url.hash}`;
          } catch (error) {
            // Ignore invalid URLs on card links
          }
        });
    }

    initSwiper() {
      if (!this.swiperElement || !window.Swiper) {
        return;
      }

      this.mediaPositions = Array.from(
        this.swiperElement.querySelectorAll('.swiper-wrapper > .swiper-slide')
      ).map(($slide) => {
        return Number($slide.dataset.mediaPosition);
      });

      this.swiper = new window.Swiper(this.swiperElement, {
        slidesPerView: 1,
        spaceBetween: 2,
        speed: 300,
        loop: window.matchMedia('(min-width: 700px)').matches,
        navigation: {
          prevEl: this.querySelector('[data-yg-product-card-prev]'),
          nextEl: this.querySelector('[data-yg-product-card-next]')
        }
      });
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.productCard.addEventListener(
        'mouseover',
        (event) => {
          const $swatchRoot =
            event.target instanceof Element
              ? event.target.closest('variant-swatch-king')
              : null;

          if ($swatchRoot?.closest('.yg-product-card') === this.productCard) {
            this.hoveredSwatchRoot = $swatchRoot;
          }
        },
        { capture: true, signal }
      );

      this.productCard.addEventListener(
        'vsk:swatch:hover',
        (event) => {
          if (
            event.target instanceof Element &&
            event.target.closest('.yg-product-card') === this.productCard
          ) {
            this.showVariantPreview(
              this.getVariantById(event.detail?.variantID)
            );
          }
        },
        { signal }
      );

      this.productCard.addEventListener(
        'mouseout',
        (event) => {
          const $swatchRoot =
            event.target instanceof Element
              ? event.target.closest('variant-swatch-king')
              : null;

          if ($swatchRoot?.closest('.yg-product-card') !== this.productCard) {
            return;
          }

          if (
            event.relatedTarget instanceof Node &&
            $swatchRoot.contains(event.relatedTarget)
          ) {
            return;
          }

          this.hoveredSwatchRoot = null;
          this.hideVariantPreview();
        },
        { capture: true, signal }
      );

      this.productCard.addEventListener(
        'mouseleave',
        () => {
          this.hoveredSwatchRoot = null;
          this.hideVariantPreview();
        },
        { signal }
      );

      this.productCard.addEventListener(
        'change',
        (event) => {
          const $target = event.target;

          if (
            !$target.matches(
              '.product-card__info [type="radio"][data-variant-id]'
            )
          ) {
            return;
          }

          const variant = this.getVariantById($target.dataset.variantId);

          if (variant) {
            this.applyColorVariant(variant, { speed: 0 });
          }
        },
        { capture: true, signal }
      );

      // Mobile has no hover — Swatch King also stops click bubbling, so capture tap/click
      this.productCard.addEventListener(
        'click',
        (event) => {
          const variant = this.getVariantFromSwatchKingEvent(event);

          if (!variant) {
            return;
          }

          this.applyColorVariant(variant, { speed: 300 });
        },
        { capture: true, signal }
      );

      this.sizeLinks.forEach(($link) => {
        $link.addEventListener(
          'click',
          (event) => {
            if ($link.classList.contains('is-disabled')) {
              event.preventDefault();
            }
          },
          { signal }
        );
      });
    }

    hideVariantPreview() {
      this.previewRequestId = (this.previewRequestId || 0) + 1;

      if (this.pendingPreviewImage) {
        this.pendingPreviewImage.onload = null;
        this.pendingPreviewImage.onerror = null;
        this.pendingPreviewImage = null;
      }

      if (this.variantPreview) {
        this.variantPreview.hidden = true;
        this.variantPreview.replaceChildren();
      }
    }

    showVariantPreview(variant) {
      this.hideVariantPreview();

      if (
        !this.variantPreview ||
        !this.hoveredSwatchRoot?.isConnected ||
        !window.matchMedia('(hover: hover) and (pointer: fine)').matches ||
        !variant?.featured_image?.url
      ) {
        return;
      }

      const requestId = this.previewRequestId;
      const image = new Image();

      this.pendingPreviewImage = image;
      image.className = 'yg-product-card__variant-preview-image';
      image.alt = variant.featured_image.alt || '';
      image.width = variant.featured_image.width;
      image.height = variant.featured_image.height;
      image.draggable = false;
      image.decoding = 'async';
      image.onload = () => {
        if (
          requestId !== this.previewRequestId ||
          !this.isConnected ||
          !this.hoveredSwatchRoot?.isConnected
        ) {
          return;
        }

        image.onload = null;
        image.onerror = null;
        this.pendingPreviewImage = null;
        this.variantPreview.replaceChildren(image);
        this.variantPreview.hidden = false;
      };
      image.onerror = () => {
        if (requestId === this.previewRequestId) {
          this.hideVariantPreview();
        }
      };
      image.src = variant.featured_image.url;
    }

    getVariantById(id) {
      if (!id) {
        return null;
      }

      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    getVariantColor(variant) {
      if (this.colorPosition < 0) {
        return '';
      }

      return variant?.options?.[this.colorPosition] || '';
    }

    getNormalizedValue(value) {
      return String(value || '')
        .trim()
        .toLowerCase();
    }

    getSizePosition() {
      if (this.sizePosition >= 0) {
        return this.sizePosition;
      }

      const availableSizes = this.sizeLinks.map(($link) =>
        this.getNormalizedValue($link.dataset.sizeValue)
      );

      return (
        this.variants[0]?.options?.findIndex((optionValue, index) => {
          return this.variants.some((variant) =>
            availableSizes.includes(
              this.getNormalizedValue(variant.options[index])
            )
          );
        }) ?? -1
      );
    }

    findVariantBySize(sizeValue) {
      const sizePosition = this.getSizePosition();

      if (sizePosition < 0) {
        return null;
      }

      return this.variants.find((variant) => {
        const hasSelectedColor =
          this.colorPosition < 0 ||
          this.getNormalizedValue(variant.options[this.colorPosition]) ===
            this.getNormalizedValue(this.selectedColor);
        const hasSelectedSize =
          this.getNormalizedValue(variant.options[sizePosition]) ===
          this.getNormalizedValue(sizeValue);

        return variant.available && hasSelectedColor && hasSelectedSize;
      });
    }

    hasVariantBySize(sizeValue) {
      const sizePosition = this.getSizePosition();

      if (sizePosition < 0) {
        return false;
      }

      return this.variants.some((variant) => {
        const hasSelectedColor =
          this.colorPosition < 0 ||
          this.getNormalizedValue(variant.options[this.colorPosition]) ===
            this.getNormalizedValue(this.selectedColor);
        const hasSelectedSize =
          this.getNormalizedValue(variant.options[sizePosition]) ===
          this.getNormalizedValue(sizeValue);

        return hasSelectedColor && hasSelectedSize;
      });
    }

    updateSizes() {
      const $extraRow = this.querySelector('[data-size-row="extra"]');
      const blankCells = Array.from(this.querySelectorAll('[data-size-blank]'));
      let visibleExtraSizeCount = 0;

      this.sizeLinks.forEach(($link) => {
        const hasSize = this.hasVariantBySize($link.dataset.sizeValue);
        const variant = this.findVariantBySize($link.dataset.sizeValue);
        const isExtraSize = $link.hasAttribute('data-extra-size');

        if (isExtraSize) {
          $link.classList.toggle('is-hidden', !hasSize);

          if (hasSize) {
            visibleExtraSizeCount += 1;
          }
        }

        if (variant) {
          $link.href = variant.url;
          $link.classList.remove('is-disabled');
          $link.removeAttribute('aria-disabled');
        } else {
          $link.href =
            this.productCard.querySelector('.product-title')?.href || '#';
          $link.classList.add('is-disabled');
          $link.setAttribute('aria-disabled', 'true');
        }
      });

      if ($extraRow) {
        const blankCount =
          visibleExtraSizeCount > 0 ? (5 - (visibleExtraSizeCount % 5)) % 5 : 0;

        $extraRow.hidden = visibleExtraSizeCount === 0;
        blankCells.forEach(($blank, index) => {
          $blank.classList.toggle('is-hidden', index >= blankCount);
        });
      }
    }

    slideToVariantMedia(variant, speed) {
      if (!this.swiper || !variant?.featured_media_position) {
        return false;
      }

      const targetIndex = this.mediaPositions.findIndex(
        (mediaPosition) =>
          mediaPosition === Number(variant.featured_media_position)
      );

      if (targetIndex < 0) {
        return false;
      }

      if (this.swiper.params.loop && this.swiper.slideToLoop) {
        this.swiper.slideToLoop(targetIndex, speed);
      } else {
        this.swiper.slideTo(targetIndex, speed);
      }

      return true;
    }
  }

  class YGCollectionProductType extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.swiperElement = this.querySelector('[data-yg-product-type-swiper]');
      this.slideButtons = Array.from(
        this.querySelectorAll('[data-yg-product-type-index]')
      );
      this.abortController = new AbortController();
      this.productTypeDisclosures = this.getProductTypeDisclosures();
      this.targetDisclosure = this.getTargetDisclosure();
      this.targetForm = this.targetDisclosure?.closest('form');
      this.targetInputs = this.getTargetInputs();

      if (!this.targetInputs.length) {
        return;
      }

      this.hidden = false;
      this.hideProductTypeDisclosures();
      this.initSwiper();
      this.bindEvents();
      this.syncActiveState();

      requestAnimationFrame(() => {
        this.swiper?.update();
      });
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.productTypeDisclosures?.forEach(($disclosure) => {
        this.getDisclosureRoot($disclosure)?.classList.remove(
          'yg-product-type-filter-hidden'
        );
      });
    }

    getProductTypeDisclosures() {
      if (!this.section) {
        return [];
      }

      return Array.from(
        this.section.querySelectorAll('.accordion__disclosure')
      ).filter(($disclosure) => {
        return this.getDisclosureTitle($disclosure) === 'Product type';
      });
    }

    getTargetDisclosure() {
      return (
        this.productTypeDisclosures.find(($disclosure) => {
          return $disclosure
            .closest('facets-form')
            ?.hasAttribute('update-on-change');
        }) || this.productTypeDisclosures[0]
      );
    }

    getTargetInputs() {
      if (!this.targetDisclosure) {
        return [];
      }

      return Array.from(
        this.targetDisclosure.querySelectorAll(
          '.checkbox-control input[type="checkbox"]'
        )
      );
    }

    getDisclosureTitle($disclosure) {
      const title = $disclosure.querySelector('summary')?.textContent || '';

      return title.replace(/\s+/g, ' ').trim();
    }

    getDisclosureRoot($disclosure) {
      return $disclosure.closest('accordion-disclosure') || $disclosure;
    }

    hideProductTypeDisclosures() {
      this.productTypeDisclosures.forEach(($disclosure) => {
        this.getDisclosureRoot($disclosure)?.classList.add(
          'yg-product-type-filter-hidden'
        );
      });
    }

    initSwiper() {
      if (!this.swiperElement || !window.Swiper) {
        return;
      }

      const styles = window.getComputedStyle(this);
      const spaceBetween =
        Number.parseFloat(styles.getPropertyValue('--yg-product-type-gap')) ||
        16;

      this.swiper = new window.Swiper(this.swiperElement, {
        slidesPerView: 'auto',
        spaceBetween,
        speed: 300,
        loop: true,
        watchOverflow: true,
        freeMode: {
          enabled: true,
          momentum: true
        }
      });
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.addEventListener(
        'click',
        (event) => {
          const $button = event.target.closest('[data-yg-product-type-index]');

          if (!$button || !this.contains($button)) {
            return;
          }

          event.preventDefault();
          this.selectProductType(Number($button.dataset.ygProductTypeIndex));
        },
        { signal }
      );
    }

    selectProductType(index) {
      if (
        !Number.isInteger(index) ||
        index < 0 ||
        index > this.targetInputs.length
      ) {
        return;
      }

      const changedInputs = [];

      this.targetInputs.forEach(($input, inputIndex) => {
        const shouldCheck = index > 0 && inputIndex === index - 1;

        if ($input.checked !== shouldCheck) {
          $input.checked = shouldCheck;
          changedInputs.push($input);
        }
      });

      this.setActiveIndex(index);

      if (changedInputs.length) {
        this.submitFacetChange(changedInputs[0]);
      }
    }

    submitFacetChange($changedInput) {
      if (!$changedInput || !this.targetForm) {
        return;
      }

      $changedInput.dispatchEvent(new Event('change', { bubbles: true }));

      const $facetsForm = this.targetForm.closest('facets-form');

      if ($facetsForm?.hasAttribute('update-on-change')) {
        return;
      }

      if (HTMLFormElement.prototype.requestSubmit) {
        this.targetForm.requestSubmit();
      } else {
        this.targetForm.dispatchEvent(
          new Event('submit', { bubbles: true, cancelable: true })
        );
      }
    }

    syncActiveState() {
      const activeInputIndex = this.targetInputs.findIndex(
        ($input) => $input.checked
      );
      const activeIndex = activeInputIndex >= 0 ? activeInputIndex + 1 : 0;

      this.setActiveIndex(activeIndex);
    }

    setActiveIndex(activeIndex) {
      this.slideButtons.forEach(($button) => {
        const isActive =
          Number($button.dataset.ygProductTypeIndex) === activeIndex;

        $button.classList.toggle('is-active', isActive);
        $button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }
  }

  if (!window.customElements.get('yg-product-card-media')) {
    window.customElements.define('yg-product-card-media', YGProductCardMedia);
  }

  if (!window.customElements.get('yg-collection-product-type')) {
    window.customElements.define(
      'yg-collection-product-type',
      YGCollectionProductType
    );
  }

  class YGCollectionProductPriority extends HTMLElement {
    connectedCallback() {
      if (this.abortController) {
        return;
      }

      this.productList = document.getElementById(
        this.getAttribute('product-list-id')
      );

      if (!this.productList) {
        return;
      }

      this.nativeSortValues = new Set(
        (this.getAttribute('native-sort-values') || '')
          .split(',')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean)
      );
      this.abortController = new AbortController();

      document.addEventListener(
        'facet:update',
        (event) => {
          this.handleFacetUpdate(event);
        },
        { signal: this.abortController.signal }
      );

      window.addEventListener(
        'popstate',
        () => {
          this.syncPriority(new URL(window.location.href));
        },
        { signal: this.abortController.signal }
      );

      this.syncPriority(new URL(window.location.href));
    }

    disconnectedCallback() {
      this.clearPriority();
      this.abortController?.abort();
      this.abortController = null;
      this.productList = null;
      this.nativeSortValues = null;
    }

    handleFacetUpdate(event) {
      if (!event.detail?.url) {
        return;
      }

      this.syncPriority(new URL(event.detail.url, window.location.origin));
    }

    syncPriority(url) {
      const $productCards = this.getProductCards();
      const rawSortBy = (url.searchParams.get('sort_by') || '')
        .trim()
        .toLowerCase();

      this.clearPriority($productCards);

      if (!rawSortBy || this.nativeSortValues?.has(rawSortBy)) {
        return;
      }

      // 支持逗号分隔多个 handle，按 URL 中的顺序置顶
      const priorityHandles = rawSortBy
        .split(',')
        .map((handle) => handle.trim())
        .filter(Boolean);
      const total = priorityHandles.length;

      priorityHandles.forEach((handle, index) => {
        const $card = $productCards.find(($productCard) => {
          return (
            $productCard.dataset.ygPriorityHandle?.toLowerCase() === handle
          );
        });

        if ($card && !$card.classList.contains('yg-product-card--priority')) {
          $card.classList.add('yg-product-card--priority');
          $card.style.order = String(index - total);
        }
      });
    }

    clearPriority($productCards = this.getProductCards()) {
      $productCards.forEach(($productCard) => {
        $productCard.classList.remove('yg-product-card--priority');
        $productCard.style.removeProperty('order');
      });
    }

    getProductCards() {
      if (!this.productList) {
        return [];
      }

      return Array.from(
        this.productList.querySelectorAll(
          'product-card[data-yg-priority-handle]'
        )
      );
    }
  }

  if (!window.customElements.get('yg-collection-product-priority')) {
    window.customElements.define(
      'yg-collection-product-priority',
      YGCollectionProductPriority
    );
  }

  class YGCollectionLinkBoxSwiper extends HTMLElement {
    connectedCallback() {
      if (this.swiper || !window.Swiper) {
        return;
      }

      const styles = window.getComputedStyle(this);
      const spaceBetween =
        Number.parseFloat(styles.getPropertyValue('--yg-product-type-gap')) ||
        16;

      this.swiper = new window.Swiper(this, {
        slidesPerView: 'auto',
        spaceBetween,
        speed: 300,
        loop: true,
        watchOverflow: true,
        freeMode: {
          enabled: true,
          momentum: true
        }
      });

      requestAnimationFrame(() => {
        this.swiper?.update();
      });
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  class YGProductCardVariantSwatchRelocator {
    constructor() {
      this.observer = new MutationObserver((mutations) => {
        this.handleMutations(mutations);
      });
    }

    connect() {
      this.relocateAll(document);
      this.observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    handleMutations(mutations) {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(($node) => {
          if (!($node instanceof Element)) {
            return;
          }

          if ($node.matches('variant-swatch-king')) {
            this.relocate($node);
          }

          this.relocateAll($node);
        });
      });
    }

    relocateAll($root) {
      $root
        .querySelectorAll('variant-swatch-king')
        .forEach(($variantSwatch) => {
          this.relocate($variantSwatch);
        });
    }

    relocate($variantSwatch) {
      const $productCard = $variantSwatch.closest('.yg-product-card');

      if (!$productCard) {
        return;
      }

      const $favoriteSlot = $productCard.querySelector(
        '.klaviyo-favorites-plp-slot'
      );
      const $pricesVariants = $productCard.querySelector('.yg-prices-variants');
      const $priceList = $pricesVariants?.querySelector('price-list');

      if (!$favoriteSlot || !$priceList) {
        return;
      }

      const isAfterFavoriteSlot = Boolean(
        $favoriteSlot.compareDocumentPosition($variantSwatch) &
        Node.DOCUMENT_POSITION_FOLLOWING
      );

      if (!isAfterFavoriteSlot) {
        return;
      }

      $priceList.insertAdjacentElement('afterend', $variantSwatch);
    }
  }

  class YGAccountLauncher extends HTMLElement {
    connectedCallback() {
      if (this.abortController) {
        return;
      }

      this.abortController = new AbortController();
      this.addEventListener('click', (event) => this.handleClick(event), {
        capture: true,
        signal: this.abortController.signal
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    findLauncher() {
      return document.querySelector(
        '.kl-hub-launcher-container button.kl-hub-launcher'
      );
    }

    handleClick(event) {
      const $trigger = event.target.closest?.('[data-yg-account-trigger]');

      if (!$trigger || !this.contains($trigger)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const $launcher = this.findLauncher();

      if ($launcher) {
        $launcher.click();
        return;
      }

      this.waitForLauncher();
    }

    waitForLauncher() {
      // Klaviyo 的 launcher 是异步注入的:等待它出现,超时后跳转账户页兜底
      if (this.isWaiting) {
        return;
      }

      this.isWaiting = true;
      this.classList.add('is-loading');

      const finish = ($launcher) => {
        clearTimeout(timer);
        observer.disconnect();
        this.isWaiting = false;
        this.classList.remove('is-loading');

        if ($launcher) {
          $launcher.click();
        } else if (this.dataset.fallbackUrl) {
          window.location.href = this.dataset.fallbackUrl;
        }
      };

      const observer = new MutationObserver(() => {
        const $launcher = this.findLauncher();

        if ($launcher) {
          finish($launcher);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      const timer = setTimeout(() => finish(this.findLauncher()), 5000);
    }
  }

  if (!window.customElements.get('yg-collection-link-box-swiper')) {
    window.customElements.define(
      'yg-collection-link-box-swiper',
      YGCollectionLinkBoxSwiper
    );
  }

  if (!window.customElements.get('yg-account-launcher')) {
    window.customElements.define('yg-account-launcher', YGAccountLauncher);
  }

  document.querySelectorAll('.stop-bubble').forEach((link) => {
    link.addEventListener('click', function (event) {
      event.stopPropagation();
    });
  });

  const productCardVariantSwatchRelocator =
    new YGProductCardVariantSwatchRelocator();
  productCardVariantSwatchRelocator.connect();

  // 商品卡折扣角标始终保持百分比文案：
  // Variant Swatch King 切换颜色时会把 "6% OFF" 重写成 "Save $20.00"，这里记录原始文案并还原
  class YGProductCardDiscountBadgeGuard {
    constructor() {
      this.percentPattern = /\d+\s*%\s*OFF/i;
      this.observer = new MutationObserver((mutations) => {
        this.handleMutations(mutations);
      });
    }

    connect() {
      this.recordAll(document);
      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    handleMutations(mutations) {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(($node) => {
            if ($node instanceof Element) {
              this.recordAll($node);
            }
          });
        }

        const $target =
          mutation.target instanceof Element
            ? mutation.target
            : mutation.target.parentElement;
        const $card = $target?.closest?.('.yg-product-card');

        if ($card) {
          this.restore($card);
        }
      });
    }

    recordAll($root) {
      const $cards = $root.matches?.('.yg-product-card')
        ? [$root]
        : Array.from($root.querySelectorAll('.yg-product-card'));

      $cards.forEach(($card) => {
        if ($card.dataset.ygDiscountText) {
          return;
        }

        const $badge = $card.querySelector('.badge-list on-sale-badge');
        const text = $badge?.textContent.trim();

        if (text && this.percentPattern.test(text)) {
          $card.dataset.ygDiscountText = text;
        }
      });
    }

    restore($card) {
      const text = $card.dataset.ygDiscountText;

      if (!text) {
        return;
      }

      $card.querySelectorAll('.badge-list on-sale-badge').forEach(($badge) => {
        if ($badge.textContent.trim() !== text) {
          $badge.textContent = text;
        }
      });
    }
  }

  new YGProductCardDiscountBadgeGuard().connect();
});
